/**
 * Remote device sessions (ENG-2470 Phase 2): a short-lived Linux desktop on
 * our network, on the site connector's network path, with Chromium open on a
 * device's web page and VLC for RTSP. Only pixels reach the user's browser.
 *
 * Contract (actuate_admin `device-session/`):
 *  - POST   device-session/            {site_id, source, ref} → {session_id, status}
 *  - GET    device-session/{id}/       → {status, ws_url?, token?, expires_at, reason?, policy?}
 *  - POST   device-session/{id}/token/ → {ws_url, token}   (reconnect; creator only)
 *  - POST   device-session/{id}/action/ {action}
 *           {action: "browser_identity", identity: "chromium" | "edge"}
 *  - POST   device-session/{id}/end/
 * The site About's `device_session_available` says whether any of it is on.
 */

/** The network-targets rows a session may open (design §6). */
export type DeviceSessionSource =
  | "camera"
  | "server"
  | "management_server"
  | "event_server"
  | "recording_server";

export const DEVICE_SESSION_SOURCES: DeviceSessionSource[] = [
  "camera",
  "server",
  "management_server",
  "event_server",
  "recording_server",
];

export type DeviceSessionStatus =
  | "starting"
  | "ready"
  | "connected"
  | "ended"
  | "failed";

/**
 * One device to open. `ref` is the camera id, the RecordingServer pk, or ""
 * for the site's own server fields. The client never sends an address:
 * admin recomputes the row and takes the address from saved fields.
 */
export interface DeviceSessionTarget {
  siteId: number;
  source: DeviceSessionSource;
  ref: string;
  /** For the dialog title only. */
  label: string;
  /** For the picker caption only; never sent. */
  address?: string | null;
  /** "Use HTTPS": the start scheme, only when the user chose one. */
  scheme?: "http" | "https";
}

/** admin returns `session_id`; `id` is an alias it may also send. */
export interface DeviceSessionStarted {
  session_id?: string;
  id?: string;
  status: DeviceSessionStatus;
}

/** customer: no clipboard; staff: paste in only (design §9.4). */
export type DeviceSessionPolicy = "customer" | "staff";

/**
 * How the remote browser presents itself to device pages (ENG-2507):
 * "edge" makes it report Microsoft Edge on Windows, for device web pages
 * that refuse other browsers. Shown to users as Chrome / Edge.
 */
export type DeviceSessionBrowserIdentity = "chromium" | "edge";

/**
 * The session noticed the device page refusing the browser, or asking for a
 * Windows plugin (ActiveX / an installer). Best effort; admin keeps the last.
 */
export interface DeviceSessionCompatHint {
  kind: "browser_refused" | "plugin_required";
  host?: string | null;
  identity?: DeviceSessionBrowserIdentity | null;
  at?: string | null;
}

/**
 * Where a session port came from (admin's allowed ports, in order). Unknown
 * values are shown without a label.
 */
export type DeviceSessionPortSourceKind =
  | "camera_http"
  | "camera_server_port"
  | "site_http_port"
  | "server_port"
  | "stream_port"
  | "milestone_http"
  | "milestone_ssl"
  | "alert_port"
  | "camera_rtsp"
  | "default_80"
  | "default_443"
  | "default_554";

export interface DeviceSessionPortSource {
  port: number;
  scheme?: "http" | "https" | "rtsp" | string | null;
  source?: DeviceSessionPortSourceKind | string | null;
}

export interface DeviceSession {
  session_id?: string;
  status: DeviceSessionStatus;
  ws_url?: string | null;
  token?: string | null;
  expires_at?: string | null;
  reason?: string | null;
  /** admin's own words for a failed session. */
  detail?: string | null;
  policy?: DeviceSessionPolicy | null;
  /** Missing on older admin: treat as "chromium". */
  browser_identity?: DeviceSessionBrowserIdentity | null;
  compat_hint?: DeviceSessionCompatHint | null;
  /** The device address the session opens. */
  target_ip?: string | null;
  /** Allowed web ports, in order; absent on older admin. */
  web_port_sources?: DeviceSessionPortSource[] | null;
  rtsp_port_sources?: DeviceSessionPortSource[] | null;
  /** The web port the session opened first. */
  start_port?: number | null;
  /** The effective start scheme. */
  scheme?: "http" | "https" | null;
}

export interface DeviceSessionToken {
  ws_url: string;
  token: string;
  expires_at?: string | null;
  policy?: DeviceSessionPolicy | null;
}

export type DeviceSessionAction = "vlc" | "open_web" | "ffprobe";

export type DeviceSessionOutcome<T> =
  | { kind: "ok"; data: T }
  | {
      kind: "refused";
      code: string;
      detail: string | null;
      retryAt: string | null;
      /** On `session_active`, only for the session's creator: reattach to it. */
      sessionId?: string | null;
    }
  | { kind: "forbidden"; code: string | null; message: string }
  | { kind: "unavailable"; detail: string | null }
  | { kind: "not_found" }
  | { kind: "error"; message: string };
